/**
 * Option lists shared by the single-player setup, the scenarios and the multiplayer lobby —
 * built for a translator, so they follow the interface language.
 */
import {
  type AiDifficulty,
  type FactionSetup,
  MAP_SIZES,
  type MapSize,
  type Season,
  type TechId,
  THEATRE_IDS,
  THEATRES,
  type TheatreId,
} from '@friction/engine';
import type { GameSpeed } from '@friction/protocol';
import type { Translator } from '../../i18n';
import { theatreTitle } from '../../i18n/names';

/** The map new setups start on: the continent of 1939 with its six great powers. */
export const DEFAULT_THEATRE: TheatreId = 'europe-1939';

/**
 * Map choices: the historical theatres first (Europe 1939 at the top), the fictional generated
 * continent last — still possible, but not what a setup is about.
 */
export function mapOptions(t: Translator): { value: TheatreId | 'generated'; label: string }[] {
  const ids = [DEFAULT_THEATRE, ...THEATRE_IDS.filter((id) => id !== DEFAULT_THEATRE)];
  return [
    ...ids.map((id) => ({ value: id, label: theatreTitle(THEATRES[id].name, t.locale) })),
    { value: 'generated' as const, label: t('sp.map.generated') },
  ];
}

export interface MapSizeInfo {
  value: MapSize;
  label: string;
  /** "64 × 44 hexes" */
  meta: string;
  /** "3–4 factions" */
  players: string;
}

export const mapSizeInfo = (t: Translator): MapSizeInfo[] =>
  (['small', 'medium', 'large'] as const).map((value) => ({
    value,
    label: t(`mapSize.${value}`),
    meta: t('mapSize.hexes', { w: MAP_SIZES[value].width, h: MAP_SIZES[value].height }),
    players: t(`mapSize.players.${value}`),
  }));

export const DIFFICULTY_IDS: readonly AiDifficulty[] = ['recruit', 'veteran', 'general'];

export const difficulties = (t: Translator): { value: AiDifficulty; label: string; title: string }[] =>
  DIFFICULTY_IDS.map((value) => ({
    value,
    label: t(`difficulty.${value}`),
    title: t(`difficulty.${value}.hint`),
  }));

export const DAY_LIMITS = [0, 10, 20, 30, 45];
export const dayLimitLabel = (t: Translator, d: number): string =>
  d ? t('dayLimit.days', { n: d }) : t('ui.none');

export type TechPreset = 'none' | 'modern';

export const TECH_PRESET_TECHS: Record<TechPreset, TechId[]> = {
  none: [],
  modern: ['field_telephone', 'radio', 'motorization'],
};

export const techPresets = (
  t: Translator,
): { value: TechPreset; label: string; title: string; techs: TechId[] }[] =>
  (['none', 'modern'] as const).map((value) => ({
    value,
    label: t(`techPreset.${value}`),
    title: t(`techPreset.${value}.hint`),
    techs: TECH_PRESET_TECHS[value],
  }));
export const techPresetOf = (techs: readonly TechId[]): TechPreset =>
  techs.includes('radio') ? 'modern' : 'none';

export const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

export const speeds = (t: Translator): { value: GameSpeed; label: string; title: string }[] =>
  ([1, 2, 3, 4, 5] as const).map((value) => ({ value, label: ROMAN[value - 1], title: t(`speed.${value}`) }));

export const seasonOptions = (t: Translator): { value: Season | 'random'; label: string; title: string }[] =>
  (['random', 'spring', 'summer', 'autumn', 'winter'] as const).map((value) => ({
    value,
    label: t(`season.${value}`),
    title: t(`season.blurb.${value}`),
  }));

export const TEAM_NAMES = ['A', 'B', 'C', 'D', 'E', 'F'];
export const teamOptions = (t: Translator): { value: number; label: string }[] =>
  TEAM_NAMES.map((name, i) => ({ value: i, label: t('team.name', { name }) }));

/**
 * What sets a scenario side apart, as a staff officer lists it: army strength, techs, reserves
 * (the AI's quality is the slot's own business). Empty for standard forces.
 */
export function sideTraits(t: Translator, f: FactionSetup): string[] {
  const parts: string[] = [];
  if (f.army !== undefined && f.army !== 1)
    parts.push(t('scn.army', { x: t.num(f.army, Number.isInteger(f.army * 10) ? 1 : 2) }));
  for (const tech of f.techs ?? []) parts.push(t(`tech.${tech}.name`));
  for (const e of f.extraUnits ?? []) parts.push(t('scn.reserve', { n: e.count, unit: t(`unit.${e.type}`) }));
  return parts;
}
